// Startpage without its own background gradient

import React from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
} from "framer-motion";
import { Laptop, Rocket, Box } from "lucide-react";
import Mountains from "@/assets/images/Mountains.svg";
import MountainsM from "@/assets/images/Mountains-m.svg";
import { FAQItem } from "../components/faqitem";
import { PerkItem } from "../components/perkitem";
import SectionTitle from "../components/sectiontitle";
import BlockLogo from "@/assets/images/Boldbrave-block.svg";

/* Helper components */
const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* FeatureCard */
const FeatureCard: React.FC<
  React.PropsWithChildren<{
    className?: string;
    title?: string;
  }>
> = ({ className = "", children, title }) => {
  const prefersReduced = useReducedMotion();
  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);
  const xSpring = useSpring(x, { stiffness: 300, damping: 40, mass: 0.6 });
  const ySpring = useSpring(y, { stiffness: 300, damping: 40, mass: 0.6 });
  const rotateX = useTransform(ySpring, [0, 1], [8, -8]);
  const rotateY = useTransform(xSpring, [0, 1], [-8, 8]);
  const spotlight = useMotionTemplate`
    radial-gradient(240px circle at ${xSpring}% ${ySpring}%, rgba(255,255,255,0.10), transparent 40%)`;

  const onMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - bounds.left) / bounds.width);
    y.set((e.clientY - bounds.top) / bounds.height);
  };

  const onLeave = () => {
    x.set(0.5);
    y.set(0.5);
  };

  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 18, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      whileHover={
        !prefersReduced
          ? {
              scale: 1.02,
              transition: { type: "spring", stiffness: 250, damping: 18 },
            }
          : {}
      }
      style={!prefersReduced ? ({ rotateX, rotateY } as any) : undefined}
      className={`relative overflow-visible rounded-2xl min-h-[230px] flex flex-col transform-gpu will-change-transform ${className}`}
    >
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[linear-gradient(135deg,#0c1110_0%,#1b2b28_100%)]" />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl"
        style={{ backgroundImage: spotlight, mixBlendMode: "screen" }}
      />
      {!prefersReduced && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-3xl opacity-20"
          animate={{ backgroundPositionX: ["0%", "120%"] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
          style={{
            backgroundImage:
              "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)",
            backgroundSize: "120% 100%",
          }}
        />
      )}
      <div className="relative z-10">
        <h4 className="text-lg font-extrabold mb-4">{title}</h4>
        <p className="font-thin pb-4 whitespace-normal break-words">
          {children}
        </p>
      </div>
    </motion.div>
  );
};

/* Animation variants */
const cardVariants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 20 },
  },
};
const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const workListVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

export default function Startpage() {
  useReducedMotion(); // currently not needed here, but fine to keep

  return (
    <div className="min-h-screen w-full text-white">
      {/* Hero */}
      <section
        id="top"
        className="relative overflow-visible pb-0 pt-16 sm:pt-24"
      >
        {/* Text (begränsad bredd) */}
        <div className="relative z-10 max-w-6xl mx-auto px-8 pt-24 pb-10 text-white">
          <div className="max-w-2xl">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-display text-6xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-[0.95]"
            >
              We Elevate <br />
              <span className="text-[#f46a3a]">Your Business</span>
            </motion.h1>
            <p className="mt-4 max-w-xl text-l leading-relaxed text-white/70">
              BoldBrave is your small-size independent digital agency, creating
              cutting-edge digital products for ourselves and our clients.
            </p>
          </div>
        </div>

        {/* Mountains full-bleed (mobile/tablet) + centered on desktop */}
        <motion.div
          className="
            relative z-0
            w-screen left-1/2 right-1/2
            -ml-[50vw] -mr-[50vw]
            lg:w-full lg:left-0 lg:right-0 lg:ml-0 lg:mr-0
          "
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Mobile + tablet (full width) */}
          <div className="block lg:hidden w-full">
            <MountainsM aria-hidden="true" className="block w-full h-auto" />
          </div>

          {/* Desktop (centered, max width) */}
          <div className="hidden lg:flex justify-center w-full">
            <div className="w-full max-w-[1200px]">
              <Mountains aria-hidden="true" className="block w-full h-auto" />
            </div>
          </div>
        </motion.div>
      </section>

      {/* We get you */}
      <section id="we-get-you" className="pt-80">
        <Container>
          <div className="mb-20">
            <BlockLogo
              aria-hidden="true"
              className="mx-auto scale-150  md:scale-200 mb-20"
            />
            <p className="mx-auto max-w-2xl text-center text-lg mb-12">
              Finding the right digital talent is harder than ever, and more
              crucial for staying ahead. So why partner with us?
            </p>
          </div>
          <motion.div
            variants={listVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="mx-auto max-w-3xl space-y-8"
          >
            {[
              {
                title: "We get your world",
                txt: "We’ve been in your shoes. We know the pressure, the deadlines, and what it takes to ship great digital work.",
              },
              {
                title: "We get your tech",
                txt: "From legacy systems to cutting-edge stacks — we speak your language and build solutions that just work.",
              },
              {
                title: "We get you",
                txt: "No buzzwords, no bullshit. Just people who listen, going the extra mile to understand, and deliver on that.",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                variants={cardVariants}
                className="flex items-start gap-6 text-left"
              >
                {/* Numrerad bubbla */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-[radial-gradient(circle_at_30%_20%,#ffffff33,#1f3b37)] flex items-center justify-center text-lg font-extrabold">
                    {index + 1}
                  </div>
                </div>

                {/* Textdel */}
                <div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-base text-white/70">{item.txt}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      {/* Work */}
      <section id="work" className="pt-30 md:pt-60">
        <Container>
          <div className="text-center mb-12">
            <SectionTitle center title="We get the work done" />
            <p className="mx-auto max-w-2xl text-lg mb-4">
              Focused product teams that design, build, and ship — forward
              leaning yet pragmatic, striving to make real impact.
            </p>
          </div>

          <motion.div
            className="
              pb-20
              -mx-4 px-4 
              flex flex-col gap-6 
              sm:mx-0 sm:px-0 
              sm:grid sm:grid-cols-3
            "
            variants={workListVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            {[
              {
                icon: <Laptop />,
                title: "Apps",
                text: "Designing and building progressive web and native apps. Commerce and other industries, B2B and B2C - we build digital products that scale and perform.",
                href: "/services/apps",
              },
              {
                icon: <Box />,
                title: "Platforms",
                text: "Beyond Backstage - designing and building developer platforms, enabling organisations to build and ship faster and with more confidence.",
                href: "/services/platforms",
              },
              {
                icon: <Rocket />,
                title: "Startups",
                text: "We join designing and building the product, as advisors or investors, depending on what moves the product forward.",
                href: "/services/startups",
              },
            ].map((p) => (
              <div key={p.title} className="w-full">
                <PerkItem
                  icon={p.icon}
                  title={p.title}
                  text={p.text}
                  href={p.href}
                />
              </div>
            ))}
          </motion.div>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16">
        <Container>
          <SectionTitle center title="Frequently Asked Questions" />
          <div className="space-y-3">
            <FAQItem
              q="What kind of companies do you usually work with?"
              a={
                <p>
                  Mostly product-driven organisations — scale-ups, digital
                  retailers, and organisations in transformation. We also
                  collaborate with ambitious founders building their first
                  version of a product. If you’re high on ambitions and outcomes
                  of your digital work, we’ll get along well.
                </p>
              }
            />
            <FAQItem
              q="Do you take on full projects or just support existing teams?"
              a={
                <p>
                  Both. Some clients hand us a business challenge and we build
                  the whole thing — strategy, design, and code. Others bring us
                  in to strengthen existing teams, accelerate delivery, or add
                  specialist skills like establishing design systems, a new
                  front-end architecture or an Developer platform.
                </p>
              }
            />
            <FAQItem
              q="How do you start a project?"
              a={
                <p>
                  We start with a short conversation to understand your goals,
                  context, and constraints. Then we run a focused discovery —
                  usually 1-2 weeks — to align on outcomes, scope, and roadmap.
                  Once we know what matters, we assemble the right team and
                  start delivering value in short cycles.
                </p>
              }
            />
            <FAQItem
              q="What’s your typical timeline?"
              a={
                <p>
                  Depends on the challenge. A design sprint or prototype might
                  be a few weeks. A product or platform build often spans
                  months. What’s constant is that we aim for early momentum,
                  short cycles, and meaningful, visible progress every single
                  week.
                </p>
              }
            />
            <FAQItem
              q="How much does it cost?"
              a={
                <p>
                  We keep things simple and transparent. You pay for the senior
                  talent you need — no inflated layers, no hidden overhead, no
                  lock-ins. We work on a monthly engagement model or fixed
                  scopes when the problem is clearly defined. Happy to talk
                  budgets early so we can plan the right approach together.
                </p>
              }
            />
            <FAQItem
              q="What makes BoldBrave different?"
              a={
                <p>
                  We’re a senior-only team that works directly with you — no
                  hand-offs, no junior pyramids, no fluff. With a business
                  mindset, a pragmatic approach - and genuine care for the
                  craft. We move fast, communicate clearly, and solve problems
                  side-by-side with your team. When you hire us, you get the
                  people on the website — and we stay with you.
                </p>
              }
            />
          </div>
        </Container>
      </section>
    </div>
  );
}
